import type { ChainId, WalletEntry } from "@src/config/walletRegistry"
import { ChainType } from "@src/hooks/useConnectWallet"
import type { Connector } from "wagmi"

export interface ConnectContext {
  signIn: (params: {
    id: ChainType
    connector?: Connector
    walletId?: string
    adapterName?: string
  }) => Promise<boolean>
  wagmiConnectors: readonly Connector[]
}

export async function connectViaResolver(
  entry: WalletEntry,
  chain: ChainId,
  ctx: ConnectContext
): Promise<boolean> {
  const resolver = entry.resolve[chain]
  if (!resolver) {
    throw new Error(`Wallet "${entry.id}" has no resolver for chain "${chain}"`)
  }

  switch (resolver.kind) {
    case "wagmi-rdns":
    case "wagmi-id": {
      const lookupId =
        resolver.kind === "wagmi-rdns" ? resolver.rdns : resolver.connectorId
      const connector = ctx.wagmiConnectors.find((c) => c.id === lookupId)
      if (!connector) {
        throw new Error(
          `No wagmi connector found for "${lookupId}" (wallet: ${entry.id})`
        )
      }
      return ctx.signIn({ id: ChainType.EVM, connector })
    }
    case "wagmi-any": {
      for (const id of resolver.tries) {
        const connector = ctx.wagmiConnectors.find((c) => c.id === id)
        if (connector) {
          return ctx.signIn({ id: ChainType.EVM, connector })
        }
      }
      throw new Error(
        `No wagmi connector found for any of [${resolver.tries.join(", ")}] (wallet: ${entry.id})`
      )
    }
    case "solana-adapter":
      return ctx.signIn({
        id: ChainType.Solana,
        adapterName: resolver.adapterName,
      })
    case "near-walletid":
      return ctx.signIn({ id: ChainType.Near, walletId: resolver.walletId })
    case "stellar-walletid":
      return ctx.signIn({ id: ChainType.Stellar, walletId: resolver.walletId })
  }
}
